import React, { useEffect, useMemo, useRef } from 'react';

import { endOfDay, isBefore } from 'date-fns';
import {
  FieldArrayWithId,
  Path,
  UseFormWatch,
  FieldArrayPath,
  UseFormSetValue,
  PathValue,
  UseFieldArrayRemove,
  UseFormRegister,
  Control,
  FieldErrors,
  UseFieldArrayAppend,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { SHARE_EXCHANGE_DETAILS_TABLE_COLUMNS, StartShareExchangeFieldNames } from 'constants/share-exchange';
import {
  DealPredefinedData,
  FormItem,
  Option,
  StartShareExchangesInvestmentItem,
  StartShareExchangesInvestmentsFormValues,
  StartShareExchangeWatchValues,
} from 'interfaces';
import ShareExchangeDetailsRow from 'page-components/deals/share-exchanges/ShareExchangeDetailsRow';
import { SnippetLayout } from 'shared-components';
import Table from 'shared-components/table';
import { calculateNoOfExchangesShares } from 'utils/investments';

import StartInvestmentShareExchangeAddNewRow from '../../my-investments/start-investment-share-exchange-modal/StartInvestmentShareExchangeAddNewRow';

interface Props<
  TFieldValues extends { exchanges: StartShareExchangesInvestmentItem[] },
  TFieldArrayName extends FieldArrayPath<StartShareExchangesInvestmentItem>,
> {
  getFields: (
    watchValues: StartShareExchangeWatchValues,
    handleSetCompanyPrice: VoidFunction,
    classOfSharesOptions: Option[],
    closingDatesOptions: Option[],
    dealIndex: number,
  ) => FormItem[];
  dealsFields: FieldArrayWithId<TFieldValues, TFieldArrayName, 'id'>[];
  shareExchanges: StartShareExchangesInvestmentItem[];
  companyDealPredefinedData: DealPredefinedData | null;
  register: UseFormRegister<TFieldValues>;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  append: UseFieldArrayAppend<StartShareExchangesInvestmentsFormValues>;
  remove: UseFieldArrayRemove;
  isFormDisabled?: boolean;
  className?: string;
}
const ShareExchangeDetailsSnippet = <
  TFieldValues extends { exchanges: StartShareExchangesInvestmentItem[] },
  TFieldArrayName extends FieldArrayPath<StartShareExchangesInvestmentItem>,
>({
  getFields,
  dealsFields,
  shareExchanges,
  companyDealPredefinedData,
  register,
  control,
  watch,
  errors,
  setValue,
  append,
  remove,
  className,
}: Props<TFieldValues, TFieldArrayName>) => {
  const classOfSharesOptions: Option[] = useMemo(() => {
    return (
      companyDealPredefinedData?.companySharePrice?.map(({ classOfShares }) => ({
        label: classOfShares,
        value: classOfShares,
      })) || []
    );
  }, [companyDealPredefinedData?.companySharePrice]);
  const endOfPageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Function to scroll to the end
    const scrollToEnd = () => {
      if (endOfPageRef.current) {
        endOfPageRef.current.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    };
    // Delay to ensure all content is rendered
    const timer = setTimeout(scrollToEnd, 200);

    return () => clearTimeout(timer);
  }, []);

  const closingDatesOptions: Option[] = useMemo(() => {
    return (
      companyDealPredefinedData?.closingDatesDropDown?.map(({ closingDate }) => ({
        label: closingDate,
        value: closingDate,
        isDisabled: isBefore(endOfDay(new Date(closingDate)), endOfDay(new Date())),
      })) || []
    );
  }, [companyDealPredefinedData?.closingDatesDropDown]);

  const appendNewInvestmentsRow = () => {
    const [{ companyPrice }] = companyDealPredefinedData?.companySharePrice || [];

    const { value: classOfShareValue } = classOfSharesOptions[0];
    const { value: closingDateValue } = closingDatesOptions[0];

    append({
      classOfShares: classOfSharesOptions?.length === 1 ? { label: classOfShareValue, value: classOfShareValue } : null,
      companyPrice: companyPrice || '0',
      fundSharePrice: companyDealPredefinedData?.fundSharePrice || '',
      noExchangedShares: '',
      noOfShares: '',
      closingDate: closingDatesOptions?.length === 1 ? { label: closingDateValue, value: closingDateValue } : null,
    });
  };

  useEffect(() => {
    if (!companyDealPredefinedData?.companySharePrice) return;
    const firstExchangePrefix = `exchanges.0`;
    const [{ companyPrice }] = companyDealPredefinedData?.companySharePrice || [];

    const noOfShares = watch(
      `${firstExchangePrefix}.${StartShareExchangeFieldNames.NO_OF_SHARES}` as Path<TFieldValues>,
    );
    const fundSharePrice = watch(
      `${firstExchangePrefix}.${StartShareExchangeFieldNames.DAXIA_PRICE}` as Path<TFieldValues>,
    );

    if (classOfSharesOptions?.length === 1) {
      const { value } = classOfSharesOptions[0];
      const optionToSet: Option = { label: value, value };

      setValue(
        `${firstExchangePrefix}.${StartShareExchangeFieldNames.CLASS_OF_SHARES}` as Path<TFieldValues>,
        optionToSet as PathValue<TFieldValues, Path<TFieldValues>>,
      );
    }

    if (closingDatesOptions?.length === 1) {
      const { value } = closingDatesOptions[0];
      const optionToSet: Option = { label: value, value };

      setValue(
        `${firstExchangePrefix}.${StartShareExchangeFieldNames.CLOSING_DATE}` as Path<TFieldValues>,
        optionToSet as PathValue<TFieldValues, Path<TFieldValues>>,
      );
    }

    if (companyPrice) {
      setValue(
        `${firstExchangePrefix}.${StartShareExchangeFieldNames.COMPANY_PRICE}` as Path<TFieldValues>,
        companyPrice as PathValue<TFieldValues, Path<TFieldValues>>,
      );
      setValue(
        `${firstExchangePrefix}.${StartShareExchangeFieldNames.NO_OF_EXCHANGED_SHARES}` as Path<TFieldValues>,
        calculateNoOfExchangesShares({
          companyPrice,
          noOfShares: `${noOfShares}`,
          fundSharePrice: `${fundSharePrice}`,
        }) as PathValue<TFieldValues, Path<TFieldValues>>,
      );
    }
  }, [
    setValue,
    shareExchanges,
    classOfSharesOptions,
    closingDatesOptions,
    companyDealPredefinedData?.companySharePrice,
    watch,
  ]);

  return (
    <SnippetLayout
      title={companyDealPredefinedData?.companyName}
      description='Please confirm the class of shares and number of shares you’d like to exchange'
      className={twMerge('py-2 3xl:py-4 [&>div_p]:p-1', className)}
      childrenClassName='mt-0'
    >
      <div className='flex flex-col 3xl:gap-2 -mt-2 3xl:mt-0'>
        <Table columns={SHARE_EXCHANGE_DETAILS_TABLE_COLUMNS} className='pt-0 mt-0'>
          {dealsFields?.map((deal, dealIndex) => (
            <ShareExchangeDetailsRow
              key={deal.id}
              deal={deal}
              dealIndex={dealIndex}
              dealsFields={dealsFields}
              watch={watch}
              setValue={setValue}
              remove={remove}
              getFields={getFields}
              classOfSharesOptions={classOfSharesOptions}
              closingDatesOptions={closingDatesOptions}
              companyDealPredefinedData={companyDealPredefinedData}
              register={register}
              control={control}
              errors={errors}
            />
          ))}
        </Table>
        <StartInvestmentShareExchangeAddNewRow addNewRow={appendNewInvestmentsRow} />
      </div>
    </SnippetLayout>
  );
};

export default ShareExchangeDetailsSnippet;
